import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, MapPin, Building2, Mail, Briefcase } from 'lucide-react';

const NUMBERS = [
  { label: 'Founded', value: '2025' },
  { label: 'Headquarters', value: 'Accra, Ghana' },
  { label: 'Coverage', value: 'All 16 regions' },
  { label: 'Languages', value: 'English (Twi, French planned)' },
];

const VALUES = [
  { title: 'Trust before scale', body: 'We would rather grow slowly with verified sellers than fast with a fraud problem. Every product decision goes through a trust review.' },
  { title: 'Built in Ghana', body: 'Our engineers and trust analysts live the market they serve. Decisions are local; technology is global.' },
  { title: 'Honest pricing', body: 'No hidden fees, no inflated commissions. Buyers and sellers see exactly what they pay before any money moves.' },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="max-w-5xl mx-auto px-4 md:px-8 pt-16 md:pt-24 pb-10">
        <p className="text-xs tracking-[0.18em] uppercase text-gold mb-3 font-medium">About</p>
        <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight">
          Building the trusted<br className="hidden md:block" /> automotive marketplace<br className="hidden md:block" /> of West Africa.
        </h1>
        <p className="mt-6 text-base md:text-lg text-muted-foreground max-w-2xl">
          AutoTrust Ghana exists because too many car buyers and sellers lose money to bad actors every year.
          We&rsquo;re an engineering-led marketplace that pairs verified profiles, escrow, and AI fraud detection
          to make every transaction feel as safe as buying from a dealership &mdash; without paying the dealership tax.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-10 md:py-12 border-t border-border/40 grid grid-cols-2 md:grid-cols-4 gap-6">
        {NUMBERS.map(({ label, value }) => (
          <div key={label} data-testid={`about-fact-${label}`}>
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-1.5">{label}</p>
            <p className="text-base md:text-lg font-semibold">{value}</p>
          </div>
        ))}
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-14 md:py-16 border-t border-border/40">
        <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2">Values</p>
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-10">What we won&rsquo;t compromise on.</h2>
        <div className="grid md:grid-cols-3 gap-8 md:gap-10">
          {VALUES.map(v => (
            <div key={v.title} data-testid={`value-${v.title}`}>
              <h3 className="text-lg font-semibold mb-2">{v.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{v.body}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-12 md:py-16 border-t border-border/40">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center text-gold mb-3"><Building2 className="w-4 h-4" /></div>
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-1">Company</p>
            <p className="text-sm">AutoTrust Ghana Ltd.</p>
          </div>
          <div>
            <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center text-gold mb-3"><MapPin className="w-4 h-4" /></div>
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-1">Address</p>
            <p className="text-sm">Accra, Greater Accra Region, Ghana</p>
          </div>
          <div>
            <div className="w-9 h-9 rounded-lg bg-gold/10 flex items-center justify-center text-gold mb-3"><Mail className="w-4 h-4" /></div>
            <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground mb-1">Contact</p>
            <a href="mailto:hello@autotrustgh.com" className="text-sm hover:underline">hello@autotrustgh.com</a>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 md:px-8 py-14 md:py-16 border-t border-border/40">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-2 flex items-center gap-1.5"><Briefcase className="w-3 h-3" /> Careers</p>
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">We&rsquo;re hiring.</h2>
            <p className="text-muted-foreground mt-2 max-w-md">Join a small team building infrastructure for a 30M-person market.</p>
          </div>
          <Link to="/careers"><Button variant="outline" className="gap-2" data-testid="about-cta-careers">View open roles <ArrowRight className="w-4 h-4" /></Button></Link>
        </div>
      </section>
    </main>
  );
}
